<template>
  <figure
    class="bg-surface ma-0 rounded-lg border border-secondary-softer border-opacity-100 overflow-hidden"
    data-cc="bekki-table"
  >
    <figcaption
      class="text-body-2 text-high-emphasis font-weight-medium px-3 pt-3 pb-1"
      data-cc="bekki-table-title"
    >
      {{ table.title }}
    </figcaption>
    <v-table density="compact">
      <thead>
        <tr>
          <th
            v-for="(column, index) in table.columns"
            :key="index"
            class="text-medium-emphasis py-2"
            :class="[
              cellPadding(index),
              { 'text-end': column.type === 'number' }
            ]"
            data-cc="bekki-table-header"
          >
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in rows"
          :key="rowIndex"
          data-cc="bekki-table-row"
        >
          <td
            v-for="(cell, columnIndex) in row"
            :key="columnIndex"
            class="text-body-2 py-2"
            :class="[
              cellPadding(columnIndex),
              {
                'bekki-table__number text-end text-no-wrap':
                  cell.type === 'number'
              }
            ]"
            data-cc="bekki-table-cell"
            :data-type="cell.type"
          >
            {{ cell.text }}
          </td>
        </tr>
      </tbody>
    </v-table>
  </figure>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type {
  ChatTable,
  ChatTableCell,
  ChatTableColumnType
} from '@/composables/api/bekki/types';
import { useUserSettings } from '@/composables/useUserSettings';

interface Props {
  table: ChatTable;
}

interface DisplayedCell {
  text: string;
  type: ChatTableColumnType;
}

const props = defineProps<Props>();

const EMPTY_CELL = '-';

const { applyDecimalPointer } = useUserSettings();

const formatCell = (cell: ChatTableCell): string => {
  if (cell === null) return EMPTY_CELL;
  if (typeof cell === 'number') {
    return applyDecimalPointer(cell, { maximumFractionDigits: 2 });
  }
  return cell;
};

const rows = computed((): DisplayedCell[][] =>
  props.table.rows.map((row) =>
    row.map((cell, index) => ({
      text: formatCell(cell),
      type: props.table.columns[index]!.type
    }))
  )
);

const cellPadding = (index: number): string => {
  if (index === 0) return 'ps-3 pe-2';
  if (index === props.table.columns.length - 1) return 'ps-2 pe-3';
  return 'px-2';
};
</script>

<style scoped>
.bekki-table__number {
  font-variant-numeric: tabular-nums;
}
</style>
